import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import {
	DELETE_GEOFENCE,
	FETCH_V4_GEOFENCES,
	SAVE_GEOFENCE,
	SAVE_GEOFENCE_DEVICES,
} from '../constants/geofence.constants';
import { WS_DATA_INIT } from '../constants/app.constants';
import { WS_DATA_SITES } from '../constants/site.constants';
import { ADMIN_PAGE } from '../../containers/AdminPage/constants';
import { GeofenceAPI } from '../../utils/API/GeofenceAPI';
import { setDetailedGeofence, v4GeofencesLoaded } from '../actions/geofence.actions';
import { selectCurrentUser } from '../selectors/app.selectors';
import { selectDevices } from '../selectors/device.selector';
import { getV4SiteUuid, v4ScopeFor } from '../../api/V4client';
import { selectSites } from '../selectors/site.selector';
import {
	deleteV4Geofence,
	fetchV4Geofences,
	getV4GeofenceDeviceIds,
	saveV4Geofence,
	syncV4GeofenceDevices,
} from '../../containers/V4Geofences/geofences';
import { lookupOrCreateDeviceV4 } from './deviceV4.saga';

const api = new GeofenceAPI();

const getField = (g, key) =>
	g && typeof g.get === 'function' ? g.get(key) : g?.[key];

function isV4Geofence(g) {
	return getField(g, 'source') === 'v4' || !!getField(g, 'v4Data');
}

function* resolveV4ScopeForCurrentUser() {
	const user = yield select(selectCurrentUser());
	if (!user?.accountId) return null;
	try {
		return yield call(() => v4ScopeFor(user.accountId));
	} catch (_) {
		return null;
	}
}

function* resolveDeviceUuid(rawDeviceId) {
	if (rawDeviceId == null) return null;
	const idStr = String(rawDeviceId);
	if (idStr.length > 20 && /[^0-9]/.test(idStr)) return idStr;

	const numeric = Number(rawDeviceId);
	if (!Number.isFinite(numeric)) return idStr;

	const devices = yield select(selectDevices());
	const device = devices?.find?.(d => +d.id === numeric);
	const existingUuid = device?.get?.('uuid') || device?.uuid;
	if (existingUuid) return existingUuid;

	try {
		return yield call(lookupOrCreateDeviceV4, numeric);
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn(`Failed to resolve V4 UUID for device ${numeric}:`, e?.message || e);
		return null;
	}
}

function* saveV4GeofenceSaga(geofence) {
	const scope = yield* resolveV4ScopeForCurrentUser();
	yield call(saveV4Geofence, geofence, scope);
	yield* fetchV4GeofencesForUserSaga();
}

function* saveGeofence({ geofence }) {
	if (isV4Geofence(geofence)) {
		yield* saveV4GeofenceSaga(geofence);
		yield put(setDetailedGeofence(null));
		return;
	}
	if (geofence.id) {
		yield api.updateGeofence(geofence);
	} else {
		yield api.addGeofence(geofence);
	}
	yield put(setDetailedGeofence(null));
}

function* saveV4GeofenceDevicesSaga(geofence, deviceIds) {
	const scope = yield* resolveV4ScopeForCurrentUser();
	const geofenceId = getField(geofence, 'id');
	if (!geofenceId) return;

	const idArray = (deviceIds?.toJS ? deviceIds.toJS() : deviceIds) || [];
	const uuids = [];
	for (const rawId of idArray) {
		const uuid = yield* resolveDeviceUuid(rawId);
		if (uuid) uuids.push(uuid);
	}
	const prevUuids = getV4GeofenceDeviceIds(geofence);
	yield call(syncV4GeofenceDevices, geofenceId, uuids, prevUuids, scope);
	yield* fetchV4GeofencesForUserSaga();
}

function* saveGeofenceDevices({ geofence, devices, teamIds }) {
	if (isV4Geofence(geofence)) {
		yield* saveV4GeofenceDevicesSaga(geofence, devices);
		return;
	}
	yield api.updateGeofenceDevices(geofence, devices.toJS(), teamIds);
}

function* deleteGeofence({ geofence }) {
	// ConfirmDialog passes [askDelete]; card flow passes a single record.
	const list = (Array.isArray(geofence) ? geofence : [geofence]).filter(Boolean);
	const v3Geofences = list.filter(g => !isV4Geofence(g));
	const v4Geofences = list.filter(isV4Geofence);

	if (v3Geofences.length) {
		yield all(v3Geofences.map(g => api.deleteGeofence(g)));
	}
	if (v4Geofences.length) {
		const scope = yield* resolveV4ScopeForCurrentUser();
		yield all(v4Geofences.map(g => call(deleteV4Geofence, g, scope)));
		yield* fetchV4GeofencesForUserSaga();
	}
}

// Skipped on the admin flow — sub-account iteration lives elsewhere.
export function* fetchV4GeofencesForUserSaga() {
	try {
		const adminState = yield select(state => state[ADMIN_PAGE]);
		const rawAccounts = adminState?.get?.('accounts') || [];
		const v3Accounts = rawAccounts.toArray
			? rawAccounts.toArray()
			: Array.isArray(rawAccounts)
			? rawAccounts
			: [];
		if (v3Accounts.length) return;

		const user = yield select(selectCurrentUser());
		if (!user || (!user.accountId && !user.isV4Native)) {
			yield put(v4GeofencesLoaded([]));
			return;
		}

		// V4-native has no V3 account to resolve — V4client supplies the scope.
		let v4Scope = null;
		if (!user.isV4Native) {
			try {
				v4Scope = yield call(() => v4ScopeFor(user.accountId));
			} catch (_) {
				yield put(v4GeofencesLoaded([]));
				return;
			}
		}

		// Both directions: uuid->id for loaded-geofence reverse lookup, id->uuid
		// (exposed via redux) so the editor can filter the Site dropdown.
		const sites = yield select(selectSites());
		const v3IdUuidPairs = yield all(
			(sites?.toArray ? sites.toArray() : (sites || []))
				.filter(s => s?.id)
				.map(s => call(() =>
					getV4SiteUuid(s.id).then(uuid => (uuid ? [s.id, uuid] : null)).catch(() => null)
				))
		);
		const migrated = v3IdUuidPairs.filter(Boolean);
		const v3SiteIdToV4Uuid = migrated;
		const siteUuidToV3Id = new Map(migrated.map(([v3Id, uuid]) => [uuid, v3Id]));

		const v4Geofences = yield call(fetchV4Geofences, v4Scope, user.accountId, siteUuidToV3Id);
		yield put(v4GeofencesLoaded(v4Geofences, v3SiteIdToV4Uuid));
	} catch (e) {
		// eslint-disable-next-line no-console
		console.warn('Failed to fetch V4 geofences for user:', e);
		yield put(v4GeofencesLoaded([]));
	}
}

export const geofenceSagas = [
	takeLatest(SAVE_GEOFENCE, saveGeofence),
	takeLatest(SAVE_GEOFENCE_DEVICES, saveGeofenceDevices),
	takeLatest(DELETE_GEOFENCE, deleteGeofence),
	takeLatest(FETCH_V4_GEOFENCES, fetchV4GeofencesForUserSaga),
	takeLatest(WS_DATA_INIT, fetchV4GeofencesForUserSaga),
	// Refire once sites arrive so the v4↔v3 site map can populate.
	takeLatest(WS_DATA_SITES, fetchV4GeofencesForUserSaga),
];
